import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { compact, keyBy } from 'lodash';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { IDefaultReducerState } from 'types/state.interface';
import type { DrawerPayloadAction } from 'types/utils.type';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import { type TPeriod, createEmptyPeriod } from '@timeedit/types';
import api from '../services/api.service';

type TPeriodState = IDefaultReducerState & {
  allPeriods: TPeriod[];
  map: Record<number, TPeriod>;
  firstDrawer: TPeriod | null;
  secondDrawer: TPeriod | null;
};

export const initialState: TPeriodState = {
  loading: false,
  hasErrors: false,
  allPeriods: [],
  map: {},
  firstDrawer: null,
  secondDrawer: null,
};

// Slice
const slice = createSlice({
  name: 'periods',
  initialState,
  reducers: {
    fetchPeriodsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchPeriodsSuccess: (state, { payload }: PayloadAction<TPeriod[]>) => {
      state.allPeriods = payload;
      state.map = keyBy(payload, 'id');
      finishedLoadingSuccess(state);
    },
    fetchPeriodsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createPeriodRequest: (state) => {
      isLoadingRequest(state);
    },
    createPeriodFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deletePeriodRequest: (state) => {
      isLoadingRequest(state);
    },
    deletePeriodSuccess: (
      state,
      { payload: periodId }: PayloadAction<number>
    ) => {
      state.allPeriods = state.allPeriods.filter(
        (period: TPeriod) => period.id !== periodId
      );
      finishedLoadingSuccess(state);
    },
    deletePeriodFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createUnsavedPeriod: (state) => {
      if (state.firstDrawer?.id === 0) return;
      const newPeriod = createEmptyPeriod();
      state.firstDrawer = newPeriod;
      state?.allPeriods?.unshift(newPeriod);
      state.map[0] = newPeriod;
    },
    changeDataDrawer: <Key extends keyof TPeriod>(
      state: TPeriodState,
      { payload }: DrawerPayloadAction<TPeriod, Key>
    ) => {
      const { isFirstDrawer, key, value } = payload;
      if (isFirstDrawer) {
        state.firstDrawer = {
          ...state.firstDrawer,
          [key]: value,
        };
      } else {
        state.secondDrawer = {
          ...state.secondDrawer,
          [key]: value,
        };
      }
    },
    saveSuccess: (
      state,
      { payload: isFirstDrawer }: PayloadAction<boolean>
    ) => {
      if (isFirstDrawer === true) {
        state.firstDrawer = undefined;
      }
      if (isFirstDrawer === false) {
        state.secondDrawer = undefined;
      }
      finishedLoadingSuccess(state);
    },
    discardChanges: (
      state,
      {
        payload,
      }: PayloadAction<{
        isFirstDrawer: boolean;
        id: number;
      }>
    ) => {
      const { isFirstDrawer, id } = payload;
      if (isFirstDrawer) {
        state.firstDrawer = state.map[id];
      } else {
        state.secondDrawer = state.map[id];
      }
    },
    setFirstDrawer: (state, { payload }: PayloadAction<TPeriod>) => {
      const previousFirstDrawer = state.firstDrawer;
      state.firstDrawer = payload;
      if (!payload && previousFirstDrawer?.id === 0) {
        state.allPeriods = state.allPeriods.filter((p: TPeriod) => p.id !== 0);
      }
    },
    setSecondDrawer: (state, { payload }: PayloadAction<TPeriod>) => {
      state.secondDrawer = payload;
    },
    duplicatePeriod: (state, { payload }: PayloadAction<TPeriod>) => {
      if (state.firstDrawer?.id === 0) return;
      const newItem = {
        ...payload,
        id: 0,
        name: payload.name + '-copy',
        history: [],
      };
      state.firstDrawer = newItem;
      state.allPeriods.unshift(newItem);
      state.map[0] = newItem;
    },
  },
});

export default slice.reducer;

// Selectors
export const periodsLoading = (state: TRootState) => state.periods.loading;
export const periodsSelector = (state: TRootState) => state.periods.allPeriods;
export const periodsMapSelector = (state: TRootState) => state.periods.map;
export const firstDrawerSelector = (state: TRootState) =>
  state.periods.firstDrawer;
export const secondDrawerSelector = (state: TRootState) =>
  state.periods.secondDrawer;
export const selectedIdsSelector = (state: TRootState) =>
  compact([state?.periods?.firstDrawer, state?.periods?.secondDrawer]).map(
    (pr) => pr.id
  );

// Actions
export const {
  fetchPeriodsRequest,
  fetchPeriodsSuccess,
  fetchPeriodsFailure,
  createPeriodRequest,
  createPeriodFailure,
  deletePeriodRequest,
  deletePeriodSuccess,
  deletePeriodFailure,
  createUnsavedPeriod,
  changeDataDrawer,
  saveSuccess,
  discardChanges,
  setFirstDrawer,
  setSecondDrawer,
  duplicatePeriod,
} = slice.actions;

// Helper functions for date transformation
// The server stores end dates as exclusive (the end value is the first value NOT in the range),
// while the frontend displays them as inclusive. These functions convert between the two conventions.

const incrementWeekNumber = (weekNumber: number): number => {
  const YY = Math.floor(weekNumber / 100);
  const week = weekNumber % 100;
  // Use Jan 4 as anchor (always in ISO week 1 of year 20YY) to avoid the edge case
  // where dayjs().year(N) lands on Jan 1–3, which can belong to the previous ISO week year.
  const date = dayjs(`20${String(YY).padStart(2, '0')}-01-04`)
    .week(week)
    .add(1, 'week');
  return (date.weekYear() % 100) * 100 + date.week();
};

const decrementWeekNumber = (weekNumber: number): number => {
  const YY = Math.floor(weekNumber / 100);
  const week = weekNumber % 100;
  const date = dayjs(`20${String(YY).padStart(2, '0')}-01-04`)
    .week(week)
    .subtract(1, 'week');
  return (date.weekYear() % 100) * 100 + date.week();
};

const transformIntervalsForFrontend = (intervals: TPeriod['intervals']) => {
  if (!intervals || intervals.length === 0) return intervals;
  return intervals.map((interval) => {
    if (interval.end != null) {
      if (interval.type === 'DATE') {
        return { ...interval, end: interval.end - 86400 };
      }
      if (interval.type === 'WEEK_NUMBER') {
        return { ...interval, end: decrementWeekNumber(interval.end) };
      }
    }
    return interval;
  });
};

const transformIntervalsForBackend = (intervals: TPeriod['intervals']) => {
  return (intervals ?? []).map((interval) => {
    if (interval.end != null) {
      if (interval.type === 'DATE') {
        return { ...interval, end: interval.end + 86400 };
      }
      if (interval.type === 'WEEK_NUMBER') {
        return { ...interval, end: incrementWeekNumber(interval.end) };
      }
    }
    return interval;
  });
};

export const fetchPeriods = createAppAsyncThunk(
  'periods/fetchPeriods',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchPeriodsRequest());
      const periods = await api.get({ endpoint: '/periods?showHidden=true' });

      // Handle the exceptional case for date range periods to align with the current behavior in te-server and te-viewer:
      // the server stores the end date as inclusive, while the frontend represents it as exclusive
      // (only dates strictly before the end date are considered included in the range).
      // transformIntervalsForFrontend converts from the server's inclusive end date to the frontend's exclusive end date,
      // and transformIntervalsForBackend performs the inverse conversion.
      const transformPeriods = (periods?.results ?? []).map(
        (period: TPeriod) => ({
          ...period,
          intervals: transformIntervalsForFrontend(period.intervals),
        })
      );
      dispatch(fetchPeriodsSuccess(transformPeriods));
    } catch (e) {
      dispatch(fetchPeriodsFailure());
      return console.error(e);
    }
  }
);

type TSavePeriodPayload = {
  period: TPeriod;
  isFirstDrawer: boolean;
};

export const savePeriod = createAppAsyncThunk<void, TSavePeriodPayload>(
  'periods/savePeriod',
  async ({ isFirstDrawer, period }, { dispatch }) => {
    try {
      dispatch(createPeriodRequest());
      const { id, history, intervals, ...periodBody } = period;
      const bodySave = periodBody as TPeriod;

      if (intervals && intervals.length > 0) {
        // Handle the exceptional case for date range periods to align with the current behavior in te-server and te-viewer.
        // The server expects the end date to be exclusive (one day after the last included date),
        // so we add 1 day to the end date before saving it.
        bodySave.intervals = transformIntervalsForBackend(intervals);
      }

      if (id === 0) {
        await api.post({
          endpoint: `/periods`,
          data: bodySave,
        });
      } else {
        await api.patch({
          endpoint: `/periods/${period.id}`,
          data: bodySave,
        });
      }
      dispatch(saveSuccess(isFirstDrawer));
      dispatch(fetchPeriods());
    } catch (e: any) {
      dispatch(createPeriodFailure());
      return console.error(e);
    }
  }
);

type TDeletePeriodPayload = {
  periodId: number;
  isFirstDrawer?: boolean;
};

export const deletePeriod = createAppAsyncThunk<void, TDeletePeriodPayload>(
  'periods/deletePeriod',
  async ({ isFirstDrawer, periodId }, { dispatch }) => {
    try {
      dispatch(deletePeriodRequest());
      await api.delete({
        endpoint: `/periods/${periodId}`,
      });
      dispatch(deletePeriodSuccess(periodId));
      dispatch(saveSuccess(isFirstDrawer));
    } catch (e: any) {
      dispatch(deletePeriodFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const checkPeriodsSaveDisabled =
  (isFirstDrawer: boolean): TAppThunk<boolean> =>
  (_, getState) => {
    const { periods } = getState();
    const { firstDrawer, secondDrawer, map: periodsMap } = periods;
    if (isFirstDrawer && firstDrawer && periodsMap[firstDrawer.id]) {
      return checkSavedDisabled(firstDrawer, periodsMap[firstDrawer.id]);
    }
    if (!isFirstDrawer && secondDrawer && periodsMap[secondDrawer.id]) {
      return checkSavedDisabled(secondDrawer, periodsMap[secondDrawer.id]);
    }

    return true;
  };

export const checkFirstDrawerExist = (): TAppThunk<boolean> => (_, getState) =>
  !!getState().periods.firstDrawer;
